import { memo } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";

import { useFindingStore, Finding } from "stores/FindingStore";

import useAnalytics from "effect/use_analytics";

import DescriptionRow from "./Row/Description";
import DetailsRow from "./Row/Details";
import FixRow from "./Row/Fix";
import NameRow from "./Row/Name";
import ServiceRow from "./Row/Service";
import SeverityRow from "./Row/Severity";
import StateRow from "./Row/State";
import TimeRow from "./Row/Time";
import UndoFixRow from "./Row/UndoFix";

// -----------------------------------------------------------------------------

const PaperSX = {
  margin: "auto",
  position: "relative",
  width: "100%",
  "@media screen and (max-width:800px)": {
    "& .MuiBox-root": {
      flexDirection: "column",
    },
  },
};

const DashboardPaperSX = {
  margin: "auto",
  maxWidth: 640,
  width: "100%",
  "@media screen and (max-width:640px)": {
    borderRadius: 0,
    height: "100%",
    overflowY: "auto",
    width: "100%",

    "& .MuiBox-root": {
      flexDirection: "column",
    },
  },
};

// -----------------------------------------------------------------------------

const FindingDetails = memo(function FindingDetails({
  finding,
  onClose,
  isDashboard = false,
}: FindingDetailsProps): JSX.Element {
  useAnalytics("Findings: Findings View");

  //
  const [store] = useFindingStore();
  const { findingTypes } = store;

  //
  return (
    <Paper sx={isDashboard ? DashboardPaperSX : PaperSX}>
      <ServiceRow
        finding={finding}
        onClose={onClose}
        isDashboard={isDashboard}
      />
      <Divider />
      <NameRow name={finding.name} />
      <Divider />
      <SeverityRow severity={finding.severity} />
      <Divider />
      <StateRow state={finding.state} />
      <Divider />
      <DescriptionRow findingType={findingTypes[finding.name]} />
      <Divider />
      <TimeRow
        eventTime={finding.event_time}
        createTime={finding.create_time}
      />
      <Divider />
      <DetailsRow finding={finding} />
      {finding.fix != null ? (
        <>
          <Divider />
          <FixRow {...finding.fix} />
        </>
      ) : null}
      {finding.fix != null && finding.fix_undo ? (
        <>
          <Divider />
          <UndoFixRow {...finding.fix} />
        </>
      ) : null}
      {isDashboard && (
        <Box sx={{ p: 2 }}>
          <Button
            onClick={() => onClose?.(finding.uuid)}
            variant="contained"
            fullWidth
          >
            Close
          </Button>
        </Box>
      )}
    </Paper>
  );
});

export default FindingDetails;

// -----------------------------------------------------------------------------

interface FindingDetailsProps {
  finding: Finding;
  onClose?: (uuid: string) => void;
  isDashboard?: boolean;
}
